import sum from 'lodash/sum';
import { useCallback, useMemo } from 'react';

import { getCornersCounts } from './SidePanelCornerRadiusTab.utils';

import { useCornerRadii } from '~/api/easy-estimator-api/corner-radii-api/hooks/useCornerRadii';
import { SELECTION_CIRCLES, UNIT_MEASURE } from '~/core/constants';
import { CircleAction, DesignSection } from '~/core/enums';
import { CornerRadiusModel } from '~/core/models';
import {
  formatPriceUnitOrFree,
  getProductDescription,
  imgUrl,
} from '~/core/utils';
import { useAppDispatch } from '~/redux/hooks';
import { updateSelectedCornerRadius } from '~/redux/slices/cornerRadii';
import { resetSelectedCustomSide } from '~/redux/slices/customSide';
import { resetSelectedDiagonalCorner } from '~/redux/slices/diagonalCorner';
import { updateSelectionCircles } from '~/redux/slices/fabric';
import { useDrawingShapeContext } from '~contexts';
import SidePanelSectionTab from '~organisms/SidePanelSectionTab';

const SidePanelCornerRadiusTab = () => {
  const dispatch = useAppDispatch();
  const {
    cornerRadii,
    isFetchingCornerRadii,
    defaultValue: defaultOption,
  } = useCornerRadii();
  const {
    getters: { actualDrawingShapes },
  } = useDrawingShapeContext();

  const handleUpdate = useCallback(
    (action: CircleAction) => (corner: CornerRadiusModel) => {
      dispatch(resetSelectedCustomSide());
      dispatch(resetSelectedDiagonalCorner());
      dispatch(updateSelectedCornerRadius(corner));
      dispatch(
        updateSelectionCircles({
          selection: SELECTION_CIRCLES.FOR_ROUNDED_RADII,
          action,
        })
      );
    },
    [dispatch]
  );

  const optionsCounters = useMemo(
    () =>
      getCornersCounts(
        actualDrawingShapes,
        cornerRadii,
        defaultOption?.productId
      ),
    [actualDrawingShapes, cornerRadii, defaultOption?.productId]
  );

  return (
    <>
      <SidePanelSectionTab
        section={DesignSection.CORNER_RADIUS}
        hookArgs={{
          options: cornerRadii,
          isFetching: isFetchingCornerRadii,
          stateAddOption: handleUpdate(CircleAction.ADD),
          stateRemoveOption: handleUpdate(CircleAction.REMOVE),
          optionsCounters,
          defaultOption,
          ignoreDefaultOptionOnRemoval: true,
          getId: (option) => option.productId,
          countersLimit: sum(Object.values(optionsCounters)),
          mapOptionToDisplay: (option: CornerRadiusModel) => ({
            title: getProductDescription(option),
            image: imgUrl(option.imageUrl, 300),
            price: formatPriceUnitOrFree(
              option.price,
              option.unitOfMeasure || UNIT_MEASURE.CORNER_RADIUS,
              true
            ),
          }),
        }}
      />
    </>
  );
};

export default SidePanelCornerRadiusTab;
